import { TemplateFieldsDisplayFn } from '../../../../models/template.model';
import { ReportRecord } from '../../../../models/report.model';
import { areaUomAbbrev, concatValuesBy, defaultTxt, formatNumber, isMasked } from '@miq/utils';
import {
  COMMON_UOM_MAP,
  displayFnWrapper,
  formatByUom,
  formatQuotingPrice,
  getStatus,
  getUsageSubType,
  mapAndFormatDynamicValue,
} from '../../utils/dynamic-data-retrievers';
import { digger } from '@cbre/digger';
import { map } from 'rxjs/operators';
import { YIELD_DETAILS } from '../common';
import {
  defaultDisplayFn,
  getCapCollarFixedLabel,
  getCapCollarFixedValuesAsString,
  getRentReviewProvisions,
  PLURAL_UOM_LABELS,
  rentReviewHasStartDate,
  SINGULAR_UOM_LABELS,
} from './uk-long-income-utils';

export const displayFnMap: TemplateFieldsDisplayFn = {
  // Porperty name
  49: {
    displayFn: (record: ReportRecord, _options, arg1, arg2) =>
      concatValuesBy(defaultTxt, mapAndFormatDynamicValue(record, arg1), mapAndFormatDynamicValue(record, arg2)),
  },
  //  Address
  27: {
    displayFn: (record: ReportRecord, options, ...args) => defaultDisplayFn(record, options, args, 27),
    routeFn: ({ data: entityRecord }: ReportRecord) => {
      return ['/records', 'property', digger(entityRecord, 'property.id'), 'details'];
    },
  },
  // Header
  280: {
    displayFn: (record: ReportRecord, options, ...args) => defaultDisplayFn(record, options, args, 280),
  },
  // Property description
  16: {
    displayFn: displayFnWrapper,
  },
  // Poperty type
  2: {
    displayFn: (record: ReportRecord, _options, args) =>
      args.lovValues.pipe(map(lovs => getUsageSubType(record, args, lovs) || defaultTxt)),
  },
  // Tenure
  3: {
    displayFn: displayFnWrapper,
  },
  // Area
  4: {
    displayFn: (record: ReportRecord, _options, area) => {
      const dynamicValue = mapAndFormatDynamicValue(record, area);
      return formatByUom(Array.isArray(dynamicValue) ? dynamicValue[0] : dynamicValue);
    },
  },
  // Tenant
  5: {
    displayFn: displayFnWrapper,
  },
  // Rent Review Frequency - rrf
  547: {
    displayFn: (record: ReportRecord, _options, args) => {
      let rrfLabel = [];
      // Get the values as a list of objects
      let optionsAsListPath: string[] | string = args.path.split('.');
      optionsAsListPath = optionsAsListPath[0];

      let occurrenceAttr: string[] | string = args.field.split('/');
      occurrenceAttr = occurrenceAttr[occurrenceAttr.length - 1];
      let uomAttr: string[] | string = args.uomArgument.field.split('/');
      uomAttr = uomAttr[uomAttr.length - 1];

      let listOfRentReviewF = digger(record.data, optionsAsListPath);

      if (!listOfRentReviewF) {
        return defaultTxt;
      }

      if (!Array.isArray(listOfRentReviewF)) {
        listOfRentReviewF = [listOfRentReviewF];
      }

      //For each value get the label to show
      listOfRentReviewF.filter(rentReviewHasStartDate).forEach(rrf => {
        const rrfRecurrenceNumber = rrf[occurrenceAttr as string];
        let rrfRecurrenceUOM = rrf[uomAttr as string];

        if (rrfRecurrenceNumber) {
          const uomLabels = rrfRecurrenceNumber === 1 ? SINGULAR_UOM_LABELS : PLURAL_UOM_LABELS;
          rrfRecurrenceUOM = uomLabels[rrfRecurrenceUOM.toLowerCase()] || rrfRecurrenceUOM;

          // If the recurrence is equals to 1 show only the UOM value
          rrfLabel.push(rrfRecurrenceNumber === 1 ? rrfRecurrenceUOM : `${rrfRecurrenceNumber}-${rrfRecurrenceUOM}`);
        }
      });

      return rrfLabel.length ? rrfLabel.join(', ') : defaultTxt;
    },
  },
  // Rent Review Provision --rrp
  548: {
    displayFn: (record: ReportRecord, _options, ...args) => getRentReviewProvisions(record, args),
  },
  // Cap and Collar / Fixed %
  549: {
    displayFn: (record: ReportRecord, _options, ...args) => getCapCollarFixedValuesAsString(record, args),
    labelDisplayFn: (record: ReportRecord, ...args) => getCapCollarFixedLabel(record, args),
  },
  // WAULT (WAUTC) years
  288: {
    displayFn: (record: ReportRecord, _options, wault, wautc) => {
      const waultValue = formatNumber(mapAndFormatDynamicValue(record, wault), undefined, '.2-2');
      const wautcvalue = formatNumber(mapAndFormatDynamicValue(record, wautc), undefined, '.2-2');
      return `${waultValue} (${wautcvalue})`;
    },
  },
  // Current Rent Per Annum
  320: {
    displayFn: (record: ReportRecord, options, currentRentPerAnnum, askingRent) => {
      currentRentPerAnnum = { ...currentRentPerAnnum, dataFormat: '.0-0' };
      askingRent = { ...askingRent, dataFormat: '.0-0' };
      const currentRentValue = mapAndFormatDynamicValue(record, currentRentPerAnnum);
      const currentRentFormattedValue = formatByUom(
        Array.isArray(currentRentValue) ? currentRentValue[0] : currentRentValue,
        options.symbols,
        true
      );
      const askingRentValue = mapAndFormatDynamicValue(record, askingRent);
      const askingRentFormattedValue = formatByUom(
        Array.isArray(askingRentValue) ? askingRentValue[0] : askingRentValue,
        options.symbols,
        true
      );
      const rentUom = digger(record.data, 'ref_asking_rent_yearly_per_area_at_sale_uom_desc');
      const uomSymbols = !!rentUom ? rentUom.split('/') : null;
      const currencySymbol = !!uomSymbols && Array.isArray(uomSymbols) ? uomSymbols[0] : '';
      const areaSymbol = !!uomSymbols && Array.isArray(uomSymbols) && uomSymbols[1] ? uomSymbols[1].toLowerCase() : '';
      const areaValue = !!areaSymbol && COMMON_UOM_MAP[areaSymbol] ? COMMON_UOM_MAP[areaSymbol] : areaSymbol;

      let result: string | string[] = defaultTxt;
      if (askingRentFormattedValue != null) {
        result = formatQuotingPrice(
          currentRentFormattedValue,
          `${currencySymbol}${askingRentFormattedValue}`,
          `per ${areaValue || defaultTxt}`
        );
      }
      return result;
    },
  },
  // Quoting Price
  9: {
    displayFn: (record: ReportRecord, options, quotingPrice, rentPerSqft) => {
      const dynamicQuotingPrice = mapAndFormatDynamicValue(record, { ...quotingPrice, dataFormat: '.0-0' });
      const quotingPriceVal = formatByUom(
        Array.isArray(dynamicQuotingPrice) ? dynamicQuotingPrice[0] : dynamicQuotingPrice,
        options.symbols,
        true
      );
      const dynamicRentPerSqft = mapAndFormatDynamicValue(record, rentPerSqft);
      const rentPerSqftVal = formatByUom(
        Array.isArray(dynamicRentPerSqft) ? dynamicRentPerSqft[0] : dynamicRentPerSqft,
        options.symbols,
        true
      );
      let totalGrossAreaUom = record.associatedProperty && record.associatedProperty.total_gross_area_uom_desc;
      totalGrossAreaUom = totalGrossAreaUom && COMMON_UOM_MAP[areaUomAbbrev(totalGrossAreaUom)];

      let result: string | string[] = defaultTxt;
      if (rentPerSqftVal != null) {
        result = formatQuotingPrice(quotingPriceVal, rentPerSqftVal, `per ${totalGrossAreaUom || defaultTxt}`);
      }
      return result;
    },
  },
  // Quoting Price NIY %
  18: {
    displayFn: (record: ReportRecord, _options) => {
      const recordFormated = digger(record.data, 'arrangement_yield_detail');
      let yieldQuotingValues = [];

      if (Array.isArray(recordFormated)) {
        yieldQuotingValues = [...recordFormated];
      } else if (recordFormated) {
        yieldQuotingValues = [recordFormated];
      }

      /**
       * If one value in the list is masked all the values are masked,
       * and is not possible to know if this field is configured or not
       */
      const maskedValue = yieldQuotingValues.find(ele => isMasked(ele.yield_type_desc));
      if (!!maskedValue) {
        return maskedValue.yield_type_desc;
      }

      yieldQuotingValues = yieldQuotingValues.filter(ele => ele.yield_type_desc === YIELD_DETAILS.quoteInitialYield);
      yieldQuotingValues = yieldQuotingValues.map(ele => formatNumber(ele.yield_percent, undefined, '.2-2'));

      return yieldQuotingValues.length ? yieldQuotingValues.join(', ') : defaultTxt;
    },
  },
  // Date to Market
  171: {
    displayFn: displayFnWrapper,
  },
  // Vendor
  10: {
    displayFn: (record: ReportRecord, _options, vendor) => {
      const vendorVal = mapAndFormatDynamicValue(record, vendor);
      return Array.isArray(vendorVal) ? concatValuesBy(', ', ...vendorVal) : vendorVal;
    },
  },
  // Purchaser
  11: {
    displayFn: (record: ReportRecord, _options, arg) => {
      const purchasers = mapAndFormatDynamicValue(record, arg);
      return Array.isArray(purchasers) ? concatValuesBy(', ', ...purchasers) : purchasers;
    },
  },
  // Sale Price
  12: {
    displayFn: (record: ReportRecord, options, salePrice, transactionValue) => {
      const arg = { ...salePrice, dataFormat: '.0-0' };
      const dynamicValue = mapAndFormatDynamicValue(record, arg);
      const salePriceValue = formatByUom(
        Array.isArray(dynamicValue) ? dynamicValue[0] : dynamicValue,
        options.symbols,
        true
      );
      const transValuePerSqft = mapAndFormatDynamicValue(record, transactionValue, null, false);
      const transValuePerSqftUOM = digger(record.data, 'reported_sale_price_per_area_value_uom');
      const uomSymbols = !!transValuePerSqftUOM ? transValuePerSqftUOM.split('/') : null;
      const currencySymbol = !!uomSymbols && Array.isArray(uomSymbols) ? uomSymbols[0] : '';
      const areaSymbol = !!uomSymbols && Array.isArray(uomSymbols) && uomSymbols[1] ? uomSymbols[1].toLowerCase() : '';
      const areaValue = !!areaSymbol && COMMON_UOM_MAP[areaSymbol] ? COMMON_UOM_MAP[areaSymbol] : areaSymbol;
      let result: string | string[] = defaultTxt;
      if (salePriceValue != null) {
        result =
          transValuePerSqft != null
            ? formatQuotingPrice(
                salePriceValue,
                `${currencySymbol} ${transValuePerSqft}`,
                `per ${areaValue || defaultTxt}`
              )
            : salePriceValue;
      }
      return result;
    },
  },
  // Sale Price NIY % (EY%)
  551: {
    displayFn: (record: ReportRecord, _options, args) => {
      const field = args.field.split('/')[0];
      const yieldData = digger(record.data, field);

      /**
       * If one value in the list is masked all the values are masked,
       * and is not possible to know if this field is configured or not
       */
      const maskedValue = yieldData && yieldData.length && yieldData.find(ele => isMasked(ele.yield_type_desc));
      if (!!maskedValue) {
        return `***** (*****)`;
      }

      let niyValue =
        yieldData && yieldData.length
          ? yieldData.find(data => data.yield_type_desc === YIELD_DETAILS.initialYield)
          : null;
      niyValue =
        niyValue && niyValue.yield_percent ? formatNumber(niyValue.yield_percent, undefined, '.2-2') : defaultTxt;

      let eyValue =
        yieldData && yieldData.length
          ? yieldData.find(data => data.yield_type_desc === YIELD_DETAILS.equivalentYield)
          : null;
      eyValue = eyValue && eyValue.yield_percent ? formatNumber(eyValue.yield_percent, undefined, '.2-2') : defaultTxt;

      return `${niyValue} (${eyValue})`;
    },
  },
  // Status Date
  15: {
    displayFn: displayFnWrapper,
  },
  // Status
  17: {
    displayFn: (record: ReportRecord, _options, statusType) =>
      statusType.lovValues.pipe(map(lovs => getStatus(record, statusType, lovs) || defaultTxt)),
  },
  // Comments
  296: {
    displayFn: displayFnWrapper,
  },
};
